<template>
  <v-menu
    v-model="isOpen"
    :position-x="positionX"
    :position-y="positionY"
    absolute
    offset-y
    :close-on-content-click="false"
    :close-on-click="false"
    :z-index="15"
    data-testid="histogram-popup"
  >
    <v-card class="histogram-card" data-testid="histogram-card">
      <div class="histogram-header">
        <div class="histogram-title" data-testid="histogram-title">
          {{ itemLabel }}
        </div>
        <div class="histogram-count" data-testid="histogram-count">
          {{ $t('total_count', { count: totalCount }) }}
        </div>
      </div>
      <svg
        :width="chartWidth"
        :height="chartHeight"
        :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
        class="histogram-svg"
      >
        <rect
          v-for="(count, hour) in histogramData"
          :key="hour"
          data-testid="histogram-bar"
          :x="chartPaddingLeft + hour * barStep"
          :y="chartHeight - chartPadding - barHeight(count)"
          :width="barWidth"
          :height="barHeight(count)"
          fill="#CC0000"
          fill-opacity="0.7"
        />
        <!-- Y軸 -->
        <line
          :x1="chartPaddingLeft"
          :y1="chartPadding"
          :x2="chartPaddingLeft"
          :y2="chartHeight - chartPadding"
          stroke="#ccc"
          stroke-width="1"
        />
        <!-- Y軸ラベル -->
        <text
          data-testid="histogram-y-label"
          :x="chartPaddingLeft - 4"
          :y="chartPadding + 4"
          text-anchor="end"
          font-size="10"
          fill="#666"
        >
          {{ yAxisMax }}
        </text>
        <text
          data-testid="histogram-y-label"
          :x="chartPaddingLeft - 4"
          :y="chartHeight - chartPadding"
          text-anchor="end"
          font-size="10"
          fill="#666"
        >
          0
        </text>
        <!-- X軸ベースライン -->
        <line
          :x1="chartPaddingLeft"
          :y1="chartHeight - chartPadding"
          :x2="chartWidth - chartPadding"
          :y2="chartHeight - chartPadding"
          stroke="#ccc"
          stroke-width="1"
        />
        <!-- X軸ラベル -->
        <text
          v-for="label in axisLabels"
          :key="`label-${label}`"
          :x="chartPaddingLeft + label * barStep + barWidth / 2"
          :y="chartHeight - 2"
          text-anchor="middle"
          font-size="10"
          fill="#666"
        >
          {{ label }}
        </text>
      </svg>
    </v-card>
  </v-menu>
</template>

<i18n lang="yaml">
ja:
  total_count: "検出数: {count}"
en:
  total_count: "Detections: {count}"
</i18n>

<script>
const BAR_AREA_HEIGHT = 80;

function niceMax(value) {
  if (value <= 1) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  if (normalized <= 1) return magnitude;
  if (normalized <= 1.5) return 1.5 * magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 5) return 5 * magnitude;
  return 10 * magnitude;
}

export default {
  name: 'HistogramPopup',
  props: {
    histogramData: {
      type: Array,
      default: () => new Array(24).fill(0),
    },
    itemLabel: {
      type: String,
      default: '',
    },
    positionX: {
      type: Number,
      default: 0,
    },
    positionY: {
      type: Number,
      default: 0,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    chartWidth() { return 280; },
    chartHeight() { return 120; },
    chartPadding() { return 20; },
    chartPaddingLeft() {
      return 30;
    },
    barStep() {
      return (this.chartWidth - this.chartPaddingLeft - this.chartPadding) / 24;
    },
    barWidth() {
      return this.barStep - 1;
    },
    maxCount() {
      return Math.max(...this.histogramData, 1);
    },
    yAxisMax() {
      return niceMax(this.maxCount);
    },
    totalCount() {
      return this.histogramData.reduce((sum, count) => sum + count, 0);
    },
    axisLabels() {
      return [0, 6, 12, 18, 23];
    },
  },
  methods: {
    barHeight(count) {
      if (count === 0) return 0;
      return (count / this.yAxisMax) * BAR_AREA_HEIGHT;
    },
  },
};
</script>

<style scoped>
.histogram-card {
  padding: 8px;
  max-width: 296px;
}

.histogram-header {
  display: flex;
  align-items: baseline;
  padding: 0 4px 4px;
}

.histogram-title {
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.histogram-count {
  font-size: 10px;
  color: #9e9e9e;
  white-space: nowrap;
  margin-left: auto;
  padding-left: 8px;
  flex-shrink: 0;
}
</style>
